interface PricingProps {
  onContactClick: () => void;
}

export default function Pricing({ onContactClick }: PricingProps) {
  const plans = [
    {
      name: 'Starter',
      price: '$499',
      period: 'one-time',
      description: 'Perfect for solo court reporters starting their online presence',
      features: [
        '5-page professional website',
        'Mobile-responsive design',
        'Contact form integration',
        'Basic SEO optimization',
        'Social media links',
        '30 days of support',
        'Free domain for 1 year',
        'SSL certificate included'
      ],
      popular: false,
      cta: 'Get Started',
      icon: 'ri-seedling-line'
    },
    {
      name: 'Professional',
      price: '$899',
      period: 'one-time',
      description: 'Most popular choice for established court reporting professionals',
      features: [
        '10-page custom website',
        'Advanced SEO optimization',
        'Blog/news section',
        'Service area pages',
        'Testimonials showcase',
        'Google Maps integration',
        '90 days of support',
        'Free domain for 1 year',
        'SSL certificate included',
        'Email setup (3 accounts)',
        'Analytics dashboard'
      ],
      popular: true,
      cta: 'Get Started',
      icon: 'ri-star-line'
    },
    {
      name: 'Enterprise',
      price: '$1,499',
      period: 'one-time',
      description: 'Complete solution for court reporting firms and agencies',
      features: [
        'Unlimited pages',
        'Custom design & branding',
        'Advanced functionality',
        'Client portal integration',
        'Online scheduling system',
        'Multi-location support',
        'Priority support (6 months)',
        'Free domain for 1 year',
        'SSL certificate included',
        'Email setup (10 accounts)',
        'Advanced analytics',
        'Monthly performance reports',
        'Content updates (first 3 months)'
      ],
      popular: false,
      cta: 'Contact Us',
      icon: 'ri-vip-crown-line'
    }
  ];

  const addons = [
    {
      name: 'Monthly Maintenance',
      price: '$99/mo',
      description: 'Keep your website updated and secure',
      icon: 'ri-tools-line'
    },
    {
      name: 'Content Writing',
      price: '$150/page',
      description: 'Professional SEO-optimized content',
      icon: 'ri-article-line'
    },
    {
      name: 'Advanced SEO',
      price: '$299/mo',
      description: 'Ongoing optimization and ranking',
      icon: 'ri-line-chart-line'
    },
    {
      name: 'Social Media Setup',
      price: '$199',
      description: 'Professional profiles and branding',
      icon: 'ri-share-line'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-[#FF6B6B]/10 text-[#FF6B6B] px-6 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="ri-price-tag-3-line mr-2"></i>
            Transparent Pricing
          </div>
          <h2 className="font-['Playfair_Display'] text-5xl font-bold text-[#1A2332] mb-6">
            Choose Your Plan
          </h2>
          
          {/* Flat Fee Banner */}
          <div className="bg-gradient-to-r from-[#FF6B6B] to-[#ff5252] text-white px-8 py-4 rounded-2xl inline-block mb-6 shadow-lg">
            <div className="flex items-center gap-3">
              <i className="ri-price-tag-3-fill text-3xl"></i>
              <div className="text-left">
                <div className="text-sm font-medium opacity-90">Special Offer</div>
                <div className="text-2xl font-bold">Flat Fee - All 5 Page Websites Only $499.00</div>
              </div>
            </div>
          </div>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Affordable, transparent pricing with no hidden fees. One-time payment, lifetime ownership of your website.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 ${
                plan.popular ? 'ring-4 ring-[#FF6B6B] scale-105' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-[#FF6B6B] text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                    Most Popular
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Icon */}
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 ${
                  plan.popular ? 'bg-[#FF6B6B]' : 'bg-gray-100'
                }`}>
                  <i className={`${plan.icon} text-3xl ${plan.popular ? 'text-white' : 'text-[#FF6B6B]'}`}></i>
                </div>

                {/* Plan Name */}
                <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A2332] mb-2">
                  {plan.name}
                </h3>
                <p className="text-gray-600 text-sm mb-6 min-h-[40px]">
                  {plan.description}
                </p>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-[#1A2332]">{plan.price}</span>
                    <span className="text-gray-500 text-sm">{plan.period}</span>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={onContactClick}
                  className={`w-full py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer mb-8 ${
                    plan.popular
                      ? 'bg-[#FF6B6B] text-white hover:bg-[#ff5252] shadow-lg'
                      : 'bg-gray-100 text-[#1A2332] hover:bg-gray-200'
                  }`}
                >
                  {plan.cta}
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>

                {/* Features */}
                <div className="space-y-4">
                  <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                    What's Included:
                  </div>
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#FF6B6B] mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add-ons Section */}
        <div className="bg-white rounded-2xl shadow-lg p-12">
          <div className="text-center mb-12">
            <h3 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A2332] mb-4">
              Optional Add-ons
            </h3>
            <p className="text-gray-600">
              Enhance your website with these additional services
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {addons.map((addon, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-6 hover:bg-[#FF6B6B]/5 hover:border-[#FF6B6B] border-2 border-transparent transition-all cursor-pointer"
              >
                <div className="w-12 h-12 bg-[#FF6B6B]/10 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${addon.icon} text-2xl text-[#FF6B6B]`}></i>
                </div>
                <h4 className="font-bold text-[#1A2332] mb-2">{addon.name}</h4>
                <p className="text-sm text-gray-600 mb-4">{addon.description}</p>
                <div className="text-2xl font-bold text-[#FF6B6B]">{addon.price}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Money Back Guarantee */}
        <div className="mt-16 bg-gradient-to-r from-[#FF6B6B] to-[#ff5252] rounded-2xl p-12 text-center text-white">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-shield-check-line text-4xl text-white"></i>
          </div>
          <h3 className="font-['Playfair_Display'] text-3xl font-bold mb-4">
            100% Satisfaction Guarantee
          </h3>
          <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
            We're confident you'll love your new website. If you're not completely satisfied within 30 days, we'll refund your money—no questions asked.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-2xl"></i>
              <span>30-Day Money Back</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-2xl"></i>
              <span>No Hidden Fees</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-2xl"></i>
              <span>Lifetime Ownership</span>
            </div>
          </div>
        </div>

        {/* FAQ Preview */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">Have questions about pricing?</p>
          <button
            onClick={onContactClick}
            className="text-[#FF6B6B] font-semibold hover:underline cursor-pointer"
          >
            Contact us for a free consultation
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
